import { userDestinationsActions, userDestinationsSelectors } from '@anm/store/modules/userDestinations';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export type CustomRTMPState = {
  name: string;
  streamKey: string;
  serverUrl: string;
};

const initialState: CustomRTMPState = {
  name: '',
  serverUrl: '',
  streamKey: ''
};

type Field = keyof CustomRTMPState;

export type HandleFieldChangeProps = {
  value: string;
  field: Field;
};

type UseCustomRTMPProps = {
  onModalClose: () => void;
  kind?: string;
};

const useCustomRTMP = ({ kind, onModalClose }: UseCustomRTMPProps) => {
  const [customRTMP, setCustomRTMP] = useState<CustomRTMPState>(initialState);
  const [invalidFields, setInvalidFields] = useState<Field[]>();
  const isPending = useSelector(userDestinationsSelectors.selectDestinationsListIsPending);
  const destinationList = useSelector(userDestinationsSelectors.selectDestinationsList);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!destinationList) return;

    if (isFirstRender.current) {
      isFirstRender.current = false;

      return;
    }

    onModalClose();
  }, [destinationList?.length]);

  const dispatch = useDispatch();

  const validateFields = useCallback(() => {
    const invalidFields = Object.keys(customRTMP).filter((field: Field) => !customRTMP[field]) as Field[];
    setInvalidFields(invalidFields);

    return invalidFields;
  }, [customRTMP]);

  const handleBlur = useCallback(() => {
    if (!invalidFields) return;

    validateFields();
  }, [customRTMP, invalidFields]);

  const handleFieldChange = useCallback(
    ({ field, value }: HandleFieldChangeProps) => {
      setCustomRTMP({
        ...customRTMP,
        [field]: value
      });
    },
    [customRTMP]
  );

  const handleSubmit = () => {
    const invalidFields = validateFields();

    if (!invalidFields.length) {
      const separator = customRTMP.serverUrl.endsWith('/') ? '' : '/';

      dispatch(
        userDestinationsActions.addRTMPDestination({
          kind,
          title: customRTMP.name,
          url: `${customRTMP.serverUrl}${separator}${customRTMP.streamKey}`
        })
      );
    }
  };

  return [customRTMP, handleBlur, isPending, invalidFields, handleSubmit, handleFieldChange] as const;
};

export default useCustomRTMP;
